import { alpha, type ThemeOptions } from '@mui/material/styles';
import Avenir from 'src/assets/fonts/avenir/Avenir.woff2';

interface FontFace {
  fontFamily: string;
  fontStyle: string;
  fontDisplay: string;
  fontWeight: number;
  src: string;
  unicodeRange: string;
}

const avenir: FontFace = {
  fontFamily: 'Avenir',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 400,
  src: `
    local('Avenir'),
    local('Avenir-Regular'),
    url(${Avenir}) format('woff2')
  `,
  unicodeRange:
    'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
};

// Extend MUI theme palette with custom properties
declare module '@mui/material/styles' {
  interface TypeBackground {
    navbar: string;
  }
  interface PaletteColor {
    background?: string;
  }
}

interface ThemeWithOverrides extends ThemeOptions {
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': FontFace[];
        '*::-webkit-scrollbar': { width: string; height: string };
        '*::-webkit-scrollbar-track': { backgroundColor: string };
        '*::-webkit-scrollbar-track-piece': { backgroundColor: string };
        '*::-webkit-scrollbar-thumb': { backgroundColor: string; borderRadius: string };
        '*::-webkit-scrollbar-corner': { backgroundColor: string };
        '*::-webkit-resizer': { backgroundColor: string };
        a?: { color: string };
      };
    };
    MuiButtonGroup: {
      groupedContainedHorizontal: {
        '&:not(:last-child)': { borderRight: string };
      };
    };
    MuiInput: {
      underline: {
        '&:before': { borderBottom: string };
        '&:after': { borderBottom: string };
        '&:hover:not($disabled):not($focused):not($error):before': { borderBottom: string };
      };
    };
    MuiFormLabel: {
      root: {
        '&$focused': { color: string };
      };
    };
  };
  components?: ThemeOptions['components'];
}

const theme: ThemeWithOverrides = {
  palette: {
    mode: 'dark',
    text: {
      primary: '#ffffff',
      secondary: alpha('#ffffff', 0.7),
      disabled: alpha('#ffffff', 0.5),
    },
    background: {
      paper: '#232323',
      default: '#121212',
      navbar: '#1C1C1C',
    },
    // Akomi gray scale - use these instead of hardcoded hex values
    grey: {
      900: '#121212', // Darkest - same as background.default
      800: '#1C1C1C', // Same as background.navbar
      700: '#232323', // Same as background.paper
      600: '#2C2C2C', // Elevated surfaces
      500: '#343434', // Borders, drag-drop zones
      400: '#505050', // Hover states
      300: '#646464', // Scrollbars, muted elements
      200: '#707070', // Secondary buttons, inputs
      100: '#909090', // Light accents
    },
    divider: alpha('#ffffff', 0.12),
    action: {
      active: '#ffffff',
      hover: alpha('#ffffff', 0.08),
      selected: alpha('#ffffff', 0.16),
      disabled: alpha('#ffffff', 0.3),
      disabledBackground: alpha('#ffffff', 0.12),
    },
    primary: {
      light: '#DBB2FF',
      main: '#BB86FC',
      dark: '#3700B3',
      contrastText: '#ffffff',
    },
    secondary: {
      light: '#A8DDDA',
      main: '#67CBC5',
      dark: '#209A93',
      contrastText: '#ffffff',
    },
    error: {
      light: '#FF8A80',
      main: '#FF5252',
      dark: '#C62828',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FFB74D',
      main: '#FF9800',
      dark: '#F57C00',
      contrastText: alpha('#000000', 0.87),
    },
    info: {
      light: '#64B5F6',
      main: '#2196F3',
      dark: '#1976D2',
      contrastText: '#ffffff',
    },
    success: {
      light: '#81C784',
      main: '#4CAF50',
      dark: '#388E3C',
      contrastText: alpha('#000000', 0.87),
    },
  },
  typography: {
    fontFamily: ['Avenir', 'Roboto', '"Segoe UI"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    h1: {
      fontSize: 32,
    },
    h2: {
      fontSize: 28,
    },
    h5: {
      fontSize: 18,
    },
    button: {
      fontSize: 16,
      textTransform: 'none',
    },
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 14,
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': [avenir],
        '*::-webkit-scrollbar': { width: '6px', height: '6px' },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '*::-webkit-scrollbar-track-piece': { backgroundColor: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha('#ffffff', 0.12),
          borderRadius: '3px',
        },
        '*::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
        '*::-webkit-resizer': { backgroundColor: 'transparent' },
      },
    },
    MuiButtonGroup: {
      groupedContainedHorizontal: {
        '&:not(:last-child)': {
          borderRight: 'none',
        },
      },
    },
    MuiInput: {
      underline: {
        '&:before': {
          borderBottom: '1px solid #fff',
        },
        '&:after': {
          borderBottom: '2px solid #67CBC5',
        },
        '&:hover:not($disabled):not($focused):not($error):before': {
          borderBottom: '2px solid #67CBC5',
        },
      },
    },
    MuiFormLabel: {
      root: {
        '&$focused': {
          color: '#67CBC5',
        },
      },
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
    },
  },
};

const primaryMain = (theme.palette?.primary as { main?: string })?.main ?? '#BB86FC';
theme.overrides.MuiCssBaseline['@global'].a = { color: primaryMain };

export default theme;
